/**
 * A10 merchant text safety. Mirrored by SQL `website_check_merchant_text`;
 * both consume `__fixtures__/text-safety.json`.
 *
 * Rejected: bidi embedding/override/isolate controls, C0/C1 controls and DEL
 * (newline allowed only when `multiline`), BOM and lone surrogates.
 * LRM/RLM/ALM are allowed. Text is never trimmed or rewritten: it is stored
 * exactly as authored.
 *
 * Code points are numeric on purpose: literal or escaped bidi controls in
 * source are themselves a Trojan-Source hazard.
 */

export const WEBSITE_TEXT_MAX_LENGTH = 4000;

export type MerchantTextIssue = 'unsafe_text' | 'text_too_long' | 'invalid_slot';

export type MerchantTextCheck =
  | { readonly ok: true; readonly length: number; readonly visible: boolean }
  | { readonly ok: false; readonly code: MerchantTextIssue };

export interface MerchantTextOptions {
  readonly maxLength: number;
  readonly multiline?: boolean;
  /** Allow '' (decorative media alt). Whitespace-only is still rejected. */
  readonly allowEmpty?: boolean;
  /** Rich-text nodes: a blank run is legal; the document checks visibility. */
  readonly allowBlank?: boolean;
}

type CodePointRange = readonly [start: number, end: number];

const NEWLINE = 0x0a;

/** Bidi embeddings/overrides, bidi isolates, BOM. */
export const WEBSITE_UNSAFE_FORMAT_RANGES: readonly CodePointRange[] = [
  [0x202a, 0x202e],
  [0x2066, 0x2069],
  [0xfeff, 0xfeff],
];

/** C0 (newline handled separately), DEL and C1. */
export const WEBSITE_CONTROL_RANGES: readonly CodePointRange[] = [
  [0x0000, 0x0009],
  [0x000b, 0x001f],
  [0x007f, 0x009f],
];

const SURROGATE_RANGE: CodePointRange = [0xd800, 0xdfff];

/**
 * Explicit blank set so SQL and TS agree: spaces, newline, zero-width space
 * and the allowed direction marks (LRM, RLM, ALM).
 */
export const WEBSITE_BLANK_RANGES: readonly CodePointRange[] = [
  [0x000a, 0x000a],
  [0x0020, 0x0020],
  [0x00a0, 0x00a0],
  [0x061c, 0x061c],
  [0x1680, 0x1680],
  [0x2000, 0x200b],
  [0x200e, 0x200f],
  [0x2028, 0x2029],
  [0x202f, 0x202f],
  [0x205f, 0x205f],
  [0x3000, 0x3000],
];

function inRanges(codePoint: number, ranges: readonly CodePointRange[]): boolean {
  return ranges.some(([start, end]) => codePoint >= start && codePoint <= end);
}

/** Code-point length, matching Postgres `char_length`. */
export function merchantTextLength(value: string): number {
  let length = 0;
  for (const _char of value) length += 1;
  return length;
}

/** Mirrors SQL `website_text_is_visible`: any character outside the blank set. */
export function isVisibleMerchantText(value: string): boolean {
  for (const char of value) {
    if (!inRanges(char.codePointAt(0) ?? 0, WEBSITE_BLANK_RANGES)) return true;
  }
  return false;
}

export function checkMerchantText(value: unknown, options: MerchantTextOptions): MerchantTextCheck {
  if (typeof value !== 'string') return { ok: false, code: 'invalid_slot' };

  let length = 0;
  let hasVisible = false;
  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;
    length += 1;
    // Iterating by code point yields a lone surrogate as its own element.
    if (inRanges(codePoint, [SURROGATE_RANGE])) return { ok: false, code: 'unsafe_text' };
    if (inRanges(codePoint, WEBSITE_UNSAFE_FORMAT_RANGES)) {
      return { ok: false, code: 'unsafe_text' };
    }
    if (inRanges(codePoint, WEBSITE_CONTROL_RANGES)) return { ok: false, code: 'unsafe_text' };
    if (codePoint === NEWLINE && !options.multiline) return { ok: false, code: 'unsafe_text' };
    if (!inRanges(codePoint, WEBSITE_BLANK_RANGES)) hasVisible = true;
  }

  if (length === 0) {
    return options.allowEmpty
      ? { ok: true, length: 0, visible: false }
      : { ok: false, code: 'invalid_slot' };
  }
  if (!hasVisible && !options.allowBlank) return { ok: false, code: 'invalid_slot' };

  const maxLength = Math.min(options.maxLength, WEBSITE_TEXT_MAX_LENGTH);
  if (length > maxLength) return { ok: false, code: 'text_too_long' };
  return { ok: true, length, visible: hasVisible };
}
